import React, { useState, useEffect } from 'react';
import {
  Sparkles, User, Briefcase, Calendar, Clock, Grid3x3,
  Shield, MapPin, Activity, ChevronRight, Loader2, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { attendanceService } from '../../services/attendanceService';
import SwipeAttendance from '../../components/SwipeAttendance';

const fmtDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

const EmployeeDashboard = () => {
  const { user } = useAuth();

  const [todayRecord, setTodayRecord] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [initLoading, setInitLoading] = useState(true);

  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [result, setResult] = useState(null);

  // 'checking' | 'granted' | 'denied' | 'prompt' | 'unsupported'
  const [locationStatus, setLocationStatus] = useState('checking');
  const [requestingLocation, setRequestingLocation] = useState(false);

  const employeeId = user?.employee_id || user?.user_id;

  const loadDashboardData = async () => {
    setInitLoading(true);
    try {
      const month = new Date().getMonth() + 1;
      const year = new Date().getFullYear();
      const historyRes = await attendanceService.getEmployeeHistory(employeeId, month, year);
      const records = historyRes.data || [];
      const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
      const statusRes = await attendanceService.getMyStatus();
      const today = statusRes.data || null;
      setTodayRecord(today);

      const recent = records
        .filter((r) => r.date !== todayStr && (!today || r.id !== today.id))
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .slice(0, 4);
      setRecentActivity(recent);

      if (user?.department) {
        const schRes = await attendanceService.getSchedules(user.department);
        if (schRes.data && schRes.data.length > 0) {
          setSchedule(schRes.data[0]);
        }
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setInitLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('unsupported');
      return;
    }
    let permissionStatus;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((status) => {
          permissionStatus = status;
          setLocationStatus(status.state);
          status.onchange = () => setLocationStatus(status.state);
        })
        .catch(() => setLocationStatus('prompt'));
    } else {
      setLocationStatus('prompt');
    }
    return () => { if (permissionStatus) permissionStatus.onchange = null; };
  }, []);

  const requestLocationPermission = () => {
    setRequestingLocation(true);
    navigator.geolocation.getCurrentPosition(
      () => { setLocationStatus('granted'); setRequestingLocation(false); },
      () => { setLocationStatus('denied'); setRequestingLocation(false); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => reject(new Error('Location access is required to mark attendance. Please enable GPS/location permission and try again.')),
        { enableHighAccuracy: true, timeout: 15000 }
      );
    });
  };

  const handleMark = async (mode) => {
    if (locationStatus !== 'granted') return;
    setResult(null);
    setLocating(true);
    let coords;
    try {
      coords = await getLocation();
    } catch (err) {
      setLocating(false);
      setResult({ type: 'error', message: err.message });
      return;
    }
    setLocating(false);

    setLoading(true);
    try {
      await attendanceService.markAttendance(mode, coords.lat, coords.lng);
      setResult({ type: 'success', message: (mode === 'checkin' ? 'Check-In' : 'Check-Out') + ' Successful!' });
      loadDashboardData();
    } catch (err) {
      setResult({ type: 'error', message: err.message || 'Failed to mark attendance.' });
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = () => {
    if (!todayRecord) return 'Not Marked Yet';
    if (todayRecord.check_in && !todayRecord.check_out) return 'In Office (Logged In)';
    if (todayRecord.check_in && todayRecord.check_out) return 'Shift Completed';
    return todayRecord.status;
  };

  const canCheckIn = !todayRecord || !todayRecord.check_in;
  const canCheckOut = Boolean(todayRecord && todayRecord.check_in && !todayRecord.check_out);
  const attendanceMode = canCheckIn ? 'checkin' : canCheckOut ? 'checkout' : 'done';
  const locationBlocked = locationStatus !== 'granted';
  const busy = loading || locating;
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';

  const officeHours = schedule ? `${schedule.start_time} - ${schedule.end_time}` : '09:00 - 18:00';

  return (
    <div className="w-full bg-white">
      {/* Feature: forced location permission popup */}
      {(locationStatus === 'prompt' || locationStatus === 'denied' || locationStatus === 'unsupported') && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card shadow-elevated w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-8 h-8" />
            </div>
            <h2 className="text-lg font-black text-gray-900 mb-2">Location Access Required</h2>
            <p className="text-sm text-gray-500 mb-6">
              {locationStatus === 'denied'
                ? "Location access is blocked for this site. Please enable it from your browser's site settings, then click Allow again."
                : locationStatus === 'unsupported'
                ? 'Your browser does not support location access, which is required to mark attendance. Please use a different browser.'
                : 'To check in or check out, please allow this site to access your location.'}
            </p>
            {locationStatus !== 'unsupported' && (
              <button
                onClick={requestLocationPermission}
                disabled={requestingLocation}
                className="w-full bg-primary text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition-all hover:shadow-lg hover:-translate-y-0.5"
              >
                {requestingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                Allow Location
              </button>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 lg:divide-x lg:divide-primary/15">
        {/* Left / Main column */}
        <div className="lg:col-span-2 lg:pr-8 divide-y divide-primary/15">
          {/* Welcome */}
          <div className="relative pb-6 rounded-2xl transition-shadow duration-300 hover:shadow-md p-4 -m-4">
            <div className="relative z-10 max-w-md">
              <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full transition-transform hover:scale-105">
                <Sparkles className="w-3.5 h-3.5" /> {greeting}
              </span>
              <h1 className="text-xl sm:text-2xl font-black text-gray-900 mt-3 tracking-tight">
                Welcome, {user?.full_name}
              </h1>
              <p className="text-sm text-gray-400 mt-1">
                Stay productive, track your attendance and manage your work effortlessly.
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-6 mt-6">
                <div className="flex items-center gap-2 rounded-xl p-2 -m-2 transition-all duration-200 hover:bg-blue-50/50 hover:shadow-sm">
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:shadow-md">
                    <User className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-semibold">Role</p>
                    <p className="text-sm font-bold text-gray-800 capitalize">{user?.role || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-2 -m-2 transition-all duration-200 hover:bg-purple-50/50 hover:shadow-sm">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:shadow-md">
                    <Briefcase className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-semibold">Department</p>
                    <p className="text-sm font-bold text-gray-800">{user?.department || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-xl p-2 -m-2 transition-all duration-200 hover:bg-amber-50/50 hover:shadow-sm">
                  <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:shadow-md">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 font-semibold">Today</p>
                    <p className="text-sm font-bold text-gray-800">{fmtDate(now)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Simple illustration */}
            <svg viewBox="0 0 260 200" className="hidden md:block absolute right-0 top-0 h-full w-72 opacity-90 transition-transform duration-500 hover:scale-105">
              <circle cx="190" cy="90" r="90" fill="#EEF2FF" />
              <circle cx="60" cy="150" r="4" fill="#C7D2FE" />
              <rect x="60" y="140" width="4" height="30" rx="2" fill="#22C55E" />
              <ellipse cx="62" cy="172" rx="16" ry="4" fill="#DCFCE7" />
              <path d="M40 100 a30 30 0 0160 0 z" fill="#8B5CF6" />
              <circle cx="70" cy="90" r="16" fill="#FDE1C8" />
              <rect x="40" y="115" width="60" height="55" rx="10" fill="#7C3AED" />
              <rect x="90" y="130" width="70" height="45" rx="6" fill="#1F2937" />
              <rect x="96" y="136" width="58" height="30" rx="2" fill="#93C5FD" />
              <circle cx="200" cy="55" r="22" fill="#fff" stroke="#C4B5FD" strokeWidth="3" />
              <line x1="200" y1="55" x2="200" y2="42" stroke="#7C3AED" strokeWidth="3" strokeLinecap="round" />
              <line x1="200" y1="55" x2="210" y2="60" stroke="#7C3AED" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>

          {/* Attendance swipe */}
          <div className="pt-6 rounded-2xl transition-shadow duration-300 hover:shadow-md p-4 -m-4">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center transition-transform duration-200 hover:scale-110 hover:shadow-md">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-sm font-black text-gray-900">Attendance</h2>
                  <p className="text-xs text-gray-400">Swipe to check in or check out</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500 bg-primary/5 px-3 py-1.5 rounded-full transition-all hover:shadow-sm hover:bg-primary/10">
                <Calendar className="w-3.5 h-3.5" /> {fmtDate(now)}
              </span>
            </div>

            <div className="flex justify-center py-4">
              {initLoading ? (
                <Loader2 className="w-6 h-6 animate-spin text-primary opacity-40" />
              ) : (
                <SwipeAttendance
                  mode={attendanceMode}
                  disabled={locationBlocked}
                  busy={busy}
                  onConfirm={() => handleMark(canCheckIn ? 'checkin' : 'checkout')}
                />
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <div className="flex-1 bg-green-50 border border-green-100 rounded-xl px-4 py-3 flex items-center gap-2 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
                <span className="w-2 h-2 rounded-full bg-green-500 shrink-0 animate-pulse" />
                <div>
                  <p className="text-[11px] text-gray-500">You are currently</p>
                  <p className="text-sm font-black text-gray-900">
                    {todayRecord?.check_in ? (todayRecord.check_out ? 'Checked Out' : 'Checked In') : 'Not Checked In'}
                  </p>
                </div>
              </div>
              <div className="flex-1 bg-primary/5 rounded-xl px-4 py-3 flex items-center gap-2 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
                <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                <div>
                  <p className="text-[11px] text-gray-500">Last Check In</p>
                  <p className="text-sm font-black text-gray-900">{todayRecord?.check_in || '--'}</p>
                </div>
              </div>
            </div>

            {result && (
              <div className={'mt-4 p-4 rounded-xl border flex items-center gap-3 transition-all duration-300 animate-[fadeIn_0.3s_ease] hover:shadow-md ' + (result.type === 'success' ? 'bg-green-50 border-green-100 text-green-700' : 'bg-red-50 border-red-100 text-red-700')}>
                {result.type === 'success' ? <Activity className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                <p className="text-sm font-bold">{result.message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="lg:pl-8 divide-y divide-primary/15 mt-6 lg:mt-0">
          {/* Quick Info */}
          <div className="pb-5">
            <h3 className="text-sm font-black text-gray-900 flex items-center gap-2 mb-4">
              <Grid3x3 className="w-4 h-4 text-gray-400" /> Quick Info
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-blue-50/60 rounded-xl p-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-default">
                <Clock className="w-4 h-4 text-blue-600 mb-2" />
                <p className="text-[10px] text-gray-500 font-semibold">Office Hours</p>
                <p className="text-sm font-black text-gray-900">{officeHours}</p>
              </div>
              <div className="bg-purple-50/60 rounded-xl p-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-default">
                <Calendar className="w-4 h-4 text-purple-600 mb-2" />
                <p className="text-[10px] text-gray-500 font-semibold">Shift Timing</p>
                <p className="text-sm font-black text-gray-900">{officeHours}</p>
              </div>
            </div>
          </div>

          {/* Attendance Status */}
          <div className="py-5">
            <h3 className="text-sm font-black text-gray-900 flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-gray-400" /> Attendance Status
            </h3>
            <button className="w-full flex items-center justify-between bg-primary/5 hover:bg-primary/10 transition-all duration-200 rounded-xl p-3.5 text-left hover:shadow-md hover:-translate-y-0.5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white text-primary flex items-center justify-center shadow-soft">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[11px] text-primary font-bold">Status Today</p>
                  <p className="text-sm font-black text-gray-900">{initLoading ? 'Loading...' : getStatusText()}</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>

          {/* Location */}
          <div className="py-5">
            <h3 className="text-sm font-black text-gray-900 flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gray-400" /> Location
            </h3>
            <div className="flex items-start gap-3 rounded-xl p-2 -m-2 transition-all duration-200 hover:bg-primary/5 hover:shadow-sm">
              <div className="w-9 h-9 rounded-lg bg-primary/5 text-gray-300 flex items-center justify-center shrink-0 transition-transform duration-200 hover:scale-110">
                <MapPin className="w-4 h-4" />
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Mark your attendance to check in or out from your current location.
              </p>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="pt-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-gray-900 flex items-center gap-2">
                <Activity className="w-4 h-4 text-gray-400" /> Recent Activity
              </h3>
              <button className="text-xs font-bold text-primary hover:underline transition-all hover:scale-105">View All</button>
            </div>
            {recentActivity.length === 0 ? (
              <div className="flex items-start gap-3 rounded-xl p-2 -m-2 transition-all duration-200 hover:bg-primary/5 hover:shadow-sm">
                <div className="w-9 h-9 rounded-lg bg-primary/5 text-gray-300 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-500">No recent activity</p>
                  <p className="text-xs text-gray-400">Your attendance log will appear here.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((r) => (
                  <div key={r.id || r.date} className="flex items-center gap-3 rounded-xl p-2 -m-2 transition-all duration-200 hover:bg-primary/5 hover:shadow-sm hover:-translate-y-0.5">
                    <div className="w-9 h-9 rounded-lg bg-primary/5 text-gray-400 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-800 capitalize truncate">{r.status?.replace('_', ' ')}</p>
                      <p className="text-[11px] text-gray-400">{r.date}{r.check_in ? ' · ' + r.check_in : ''}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDashboard;